import React, { useContext, useState } from "react";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import { Loader2 } from "lucide-react";
import { API, AuthContext } from "../../App";
import { useTheme } from "../../contexts/ThemeContext";
import { useIsMobileApp } from "../../hooks/useIsMobileApp";
import useSettingsData from "../../hooks/useSettingsData";

// Tab components
import MyAccountTab from "../../components/settings/MyAccountTab";
import MyPlanTab from "../../components/settings/MyPlanTab";
import BusinessSettingsTab from "../../components/settings/BusinessSettingsTab";
import ProfitPartnersSection from "../../components/settings/ProfitPartnersSection";
import JobSourcesManager from "../../components/settings/JobSourcesManager";
import JobTypesManager from "../../components/settings/JobTypesManager";
import PaymentTypesManager from "../../components/settings/PaymentTypesManager";
import PhoneManagementTab from "../../components/settings/PhoneManagementTab";
import CallMaskingTab from "../../components/settings/CallMaskingTab";
import NotificationsTab from "../../components/settings/NotificationsTab";
import QuickBooksSettings from "../../components/settings/QuickBooksSettings";
import InviteTemplatesTab from "../../components/settings/InviteTemplatesTab";
import StripeConnectTab from "../../components/settings/StripeConnectTab";
import TapToPayCard from "../../components/settings/TapToPayCard";
import TechPanelBranding from "../../components/TechPanelBranding";

import StoreLocationsTab from "../../components/settings/StoreLocationsTab";
import ServiceCategoriesTab from "../../components/settings/ServiceCategoriesTab";
import EmailSettingsTab from "../../components/settings/EmailSettingsTab";
import ComplianceTab from "../../components/settings/ComplianceTab";
import DataImportTab from "../../components/settings/DataImportTab";
import IntegrationsTab from "../../components/settings/IntegrationsTab";
import JobStatusesTab from "../../components/settings/JobStatusesTab";
import TechReportTemplatesTab from "../../components/settings/reporttemplates/TechReportTemplatesTab";

// Layout components
import SettingsTabBar from "../../components/settings/SettingsTabBar";
import SettingsDeleteDialog from "../../components/settings/SettingsDeleteDialog";
import PaymentSuccessDialog from "../../components/settings/PaymentSuccessDialog";
import axios from "axios";

const SettingsPage = () => {
  const { user } = useContext(AuthContext);
  const { isDark } = useTheme();
  const isMobileApp = useIsMobileApp();
  const s = useSettingsData();

  const isTeamMember = user?.parent_user_id && user?.role !== "owner";
  const isDispatcher = user?.role === "dispatcher";
  const isStoreAccount = user?.account_type === "multi_location_store";
  const urlTab = new URLSearchParams(window.location.search).get("tab");
  const defaultTab = urlTab || (isDispatcher ? "services" : isTeamMember ? "business" : (isMobileApp ? "business" : "my-plan"));
  const [activeTab, setActiveTab] = useState(defaultTab);

  if (s.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FFCC00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="settings-page">
      {s.paymentSuccessSession && (
        <PaymentSuccessDialog
          sessionId={s.paymentSuccessSession}
          API={API}
          getAuthConfig={s.getAuthConfig}
          availablePlans={s.availablePlans}
          onConfirmed={async () => {
            try {
              const res = await axios.get(`${API}/auth/me`, s.getAuthConfig());
              if (res.data) { localStorage.setItem("user", JSON.stringify(res.data)); s.setUser(res.data); }
            } catch { /* non-blocking */ }
            s.refetchLimits?.();
            s.fetchPaymentHistory?.();
          }}
          onClose={() => s.setPaymentSuccessSession(null)}
        />
      )}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-foreground font-['Manrope']">Settings</h2>
        <p className="text-sm text-muted-foreground">Manage your business configuration</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
          <SettingsTabBar activeTab={activeTab} setActiveTab={setActiveTab} isTeamMember={isTeamMember} isDispatcher={isDispatcher} isStoreAccount={isStoreAccount} />

          <div className="flex-1 min-w-0">
        {/* === Tab Content === */}
        <TabsContent value="my-account">
          <MyAccountTab user={s.user} setUser={s.setUser} />
        </TabsContent>

        <TabsContent value="my-plan">
          <MyPlanTab user={s.user} setUser={s.setUser} limits={s.limits} availablePlans={s.availablePlans} availableDiscounts={s.availableDiscounts} paymentHistory={s.paymentHistory} billingPeriod={s.billingPeriod} setBillingPeriod={s.setBillingPeriod} refetchLimits={s.refetchLimits} API={API} getAuthConfig={s.getAuthConfig} />
        </TabsContent>

        <TabsContent value="business">
          <div className="space-y-6">
            <BusinessSettingsTab businessSettings={s.businessSettings} setBusinessSettings={s.setBusinessSettings} saveBusinessSettings={s.saveBusinessSettings} showDeleteModal={s.showDeleteModal} setShowDeleteModal={s.setShowDeleteModal} />
            <ProfitPartnersSection />
          </div>
        </TabsContent>

        <TabsContent value="sources">
          <JobSourcesManager jobSources={s.jobSources} sourceDialogOpen={s.sourceDialogOpen} setSourceDialogOpen={s.setSourceDialogOpen} editingSource={s.editingSource} sourceForm={s.sourceForm} setSourceForm={s.setSourceForm} resetSourceForm={s.resetSourceForm} editSource={s.editSource} saveJobSource={s.saveJobSource} confirmDelete={s.confirmDelete} onReorder={s.reorderSources} />
        </TabsContent>

        <TabsContent value="job-statuses">
          <JobStatusesTab />
        </TabsContent>

        <TabsContent value="services">
          <JobTypesManager jobTypes={s.jobTypes} suggestions={s.suggestions} businessType={s.businessSettings.business_type} jobTypeDialogOpen={s.jobTypeDialogOpen} setJobTypeDialogOpen={s.setJobTypeDialogOpen} editingJobType={s.editingJobType} jobTypeForm={s.jobTypeForm} setJobTypeForm={s.setJobTypeForm} resetJobTypeForm={s.resetJobTypeForm} editJobType={s.editJobType} saveJobType={s.saveJobType} importSuggestions={s.importSuggestions} confirmDelete={s.confirmDelete} />
        </TabsContent>

        <TabsContent value="phones">
          <PhoneManagementTab
            phoneNumbers={s.phoneNumbers} jobSources={s.jobSources} limits={s.limits} isDark={isDark} isMobileApp={isMobileApp}
            phoneLimits={s.phoneLimits} atPhoneLimit={s.atPhoneLimit} nearPhoneLimit={s.nearPhoneLimit}
            smsSettings={s.smsSettings} setSmsSettings={s.setSmsSettings} smsSettingsLoading={s.smsSettingsLoading} saveSmsSettings={s.saveSmsSettings} resetSmsTemplate={s.resetSmsTemplate}
            techReminderSettings={s.techReminderSettings} setTechReminderSettings={s.setTechReminderSettings} techReminderLoading={s.techReminderLoading} saveTechReminderSettings={s.saveTechReminderSettings} resetTechReminderTemplate={s.resetTechReminderTemplate}
            businessSettings={s.businessSettings} confirmDelete={s.confirmDelete} fetchAllSettings={s.fetchAllSettings}
            API={API} getAuthConfig={s.getAuthConfig} refetchLimits={s.refetchLimits}
            showPhoneLimitModal={s.showPhoneLimitModal} setShowPhoneLimitModal={s.setShowPhoneLimitModal}
          />
        </TabsContent>

        <TabsContent value="payments">
          <PaymentTypesManager paymentTypes={s.paymentTypes} paymentDialogOpen={s.paymentDialogOpen} setPaymentDialogOpen={s.setPaymentDialogOpen} editingPayment={s.editingPayment} paymentForm={s.paymentForm} setPaymentForm={s.setPaymentForm} resetPaymentForm={s.resetPaymentForm} editPayment={s.editPayment} savePaymentType={s.savePaymentType} togglePaymentActive={s.togglePaymentActive} confirmDelete={s.confirmDelete} />
        </TabsContent>

        <TabsContent value="stripe-connect">
          <div className="space-y-6">
            <StripeConnectTab />
            <TapToPayCard />
          </div>
        </TabsContent>

        <TabsContent value="call-masking">
          <CallMaskingTab callMaskingSettings={s.callMaskingSettings} setCallMaskingSettings={s.setCallMaskingSettings} callMaskingLoading={s.callMaskingLoading} setCallMaskingLoading={s.setCallMaskingLoading} phoneNumbers={s.phoneNumbers} limits={s.limits} API={API} getAuthConfig={s.getAuthConfig} />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsTab
            techReminderSettings={s.techReminderSettings} setTechReminderSettings={s.setTechReminderSettings} techReminderLoading={s.techReminderLoading} saveTechReminderSettings={s.saveTechReminderSettings} resetTechReminderTemplate={s.resetTechReminderTemplate}
            customerSmsSettings={s.customerSmsSettings} setCustomerSmsSettings={s.setCustomerSmsSettings} customerSmsLoading={s.customerSmsLoading} saveCustomerSmsSettings={s.saveCustomerSmsSettings}
          />
        </TabsContent>

        <TabsContent value="branding"><div className="space-y-4 sm:space-y-6"><TechPanelBranding /></div></TabsContent>

        <TabsContent value="quickbooks">
          <QuickBooksSettings limits={s.limits} onNavigateToPlans={() => { const plansTab = document.querySelector('[data-value="my-plan"]'); if (plansTab) plansTab.click(); }} />
        </TabsContent>

        <TabsContent value="templates"><InviteTemplatesTab /></TabsContent>

        <TabsContent value="pdf-reports"><TechReportTemplatesTab /></TabsContent>

        <TabsContent value="email"><EmailSettingsTab /></TabsContent>

        <TabsContent value="data-import"><DataImportTab /></TabsContent>

        <TabsContent value="integrations"><IntegrationsTab /></TabsContent>

        {/* Compliance is available for owners and admin team members.
            Dispatchers (limited scope) don't see it. */}
        {!isDispatcher && (
          <TabsContent value="compliance">
            <ComplianceTab user={s.user} headers={s.getAuthConfig().headers} />
          </TabsContent>
        )}

        {/* Store-specific tabs (multi_location_store only, not dispatchers) */}
        {isStoreAccount && !isDispatcher && (
          <>
            <TabsContent value="store-locations">
              <StoreLocationsTab isDark={isDark} headers={s.getAuthConfig().headers} />
            </TabsContent>
          </>
        )}
          </div>
        </div>
      </Tabs>

      <SettingsDeleteDialog deleteDialog={s.deleteDialog} setDeleteDialog={s.setDeleteDialog} handleDelete={s.handleDelete} />

      <p className="text-xs text-muted-foreground text-center pb-2" data-testid="support-access-note">
        Support may access your account to help with technical issues. All support access is logged.
      </p>
    </div>
  );
};

export default SettingsPage;
